import classNames from 'classnames';
import type { FunctionComponent } from 'react';
import { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';

import { Analytics } from '@trello/atlassian-analytics';
import { intl } from '@trello/i18n';
import { Button } from '@trello/nachos/button';
import { navigate } from '@trello/router/navigate';

import { getMarketingScreenInfo } from 'app/src/getMarketingScreenInfo';
import type { TemplateCategoryImagePath } from './getTemplateCategoryImage';
import { getTemplateCategoryImage } from './getTemplateCategoryImage';
import { SearchWrapper } from './SearchWrapper';
import { TemplatesGalleryBanner } from './TemplatesGalleryBanner';
import { TemplatesGalleryCategoryTile } from './TemplatesGalleryCategoryTile';
import { TemplatesTile } from './TemplatesTile';
import type { Template } from './types';
import { WhatIsTrelloBanner } from './WhatIsTrelloBanner';

import styles from './TemplatesFeatured.less';

const featuredCategoryKeys = [
  'business',
  'design',
  'education',
  'engineering',
  'marketing',
  'project-management',
  'remote-work',
];

interface GroupedTemplates {
  [category: string]: Template[];
}

export const DISPLAY_TEMPLATE_COUNT = 3;

export const groupTemplatesByCategory = (
  templates: Template[],
): GroupedTemplates => {
  const startVal = {
    business: [],
    design: [],
    education: [],
    engineering: [],
    marketing: [],
    'project-management': [],
    'remote-work': [],
  } as GroupedTemplates;

  return templates.reduce((grouped, template) => {
    const category = template.templateGallery.category;
    if (grouped[category]) {
      grouped[category].push(template);
    }
    return grouped;
  }, startVal);
};

interface TemplatesFeaturedProps {
  templates: Template[];
  isInAppGallery: boolean;
  categories: {
    key: string;
  }[];
}

const getDisplayableFeaturedTemplates = (templates: Template[]) => {
  return templates
    .filter((t) => t.templateGallery.featured)
    .sort((a, b) => b.templateGallery.precedence - a.templateGallery.precedence)
    .slice(0, DISPLAY_TEMPLATE_COUNT);
};

export const TemplatesFeatured: FunctionComponent<TemplatesFeaturedProps> = ({
  categories,
  isInAppGallery,
  templates,
}) => {
  useEffect(() => {
    const { url, referrerUrl, referrerScreenName } = getMarketingScreenInfo();
    Analytics.sendMarketingScreenEvent({
      url,
      referrerUrl,
      referrerScreenName,
      event: {
        source: 'templateGalleryScreen',
        name: 'templateGalleryScreenV2',
        attributes: {
          totalCategories: categories.length,
          totalTemplates: templates.length,
        },
      },
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const groupedTemplates = groupTemplatesByCategory(templates);
  const displayableFeaturedTemplates =
    getDisplayableFeaturedTemplates(templates);

  const featuredTemplates = (
    <div>
      <h2 className={styles.headerContainer}>
        <div
          className={classNames(styles.headerIcon, styles.newAndNotableIcon)}
        />
        <span className={styles.templateGalleryHeader}>
          <FormattedMessage
            id="templates.templates.new-and-notable-templates"
            defaultMessage="New and notable templates"
            description="New and notable templates"
          />
        </span>
      </h2>

      <div className={classNames(styles.templatesTilesContainer)}>
        {displayableFeaturedTemplates.map((t) => {
          return (
            <TemplatesTile
              key={t.id}
              avatarUrl={t.templateGallery.avatarUrl}
              avatarShape={t.templateGallery.avatarShape}
              backgroundColor={t.prefs.backgroundColor}
              backgroundUrl={t.prefs.backgroundImage}
              blurb={t.templateGallery.blurb}
              boardId={t.id}
              byline={t.templateGallery.byline}
              category={t.templateGallery.category}
              idOrganization={t.idOrganization}
              shortLink={t.shortLink}
              title={t.name}
              isFeatured
              viewCount={t.templateGallery.stats.viewCount}
              copyCount={t.templateGallery.stats.copyCount}
            />
          );
        })}
      </div>
    </div>
  );

  const categorizedTemplates = featuredCategoryKeys
    .map((c) => {
      if (groupedTemplates?.[c]?.length) {
        return (
          <div key={c}>
            <h2 className={styles.headerContainer}>
              <img
                className={styles.headerIcon}
                src={getTemplateCategoryImage(
                  c as TemplateCategoryImagePath,
                  'small',
                )}
                alt={`${intl
                  .formatMessage({
                    id: `templates.templates.${c}`,
                    defaultMessage: 'Category image',
                    description: 'Alt text for a category image',
                  })
                  .replace(/&amp;/g, '&')} Icon`}
              />
              <span className={styles.templateGalleryHeader}>
                {intl.formatMessage({
                  id: `templates.templates.${c}`,
                  defaultMessage: 'Template Gallery',
                  description:
                    'A header for the gallery of templates for a category',
                })}
              </span>

              <Button
                onClick={() => navigate(`/templates/${c}`, { trigger: true })}
                className={classNames(styles.moreTemplatesCategoryLink)}
              >
                {intl.formatMessage(
                  {
                    id: 'templates.templates.more-templates-for-category',
                    defaultMessage: 'More templates for {categoryName}',
                    description:
                      'A button that shows more templates for a category',
                  },
                  {
                    categoryName: intl
                      .formatMessage({
                        id: `templates.templates.${c}`,
                        defaultMessage: 'Category',
                        description: 'The name of a category',
                      })
                      .replace(/&amp;/g, '&'),
                  },
                )}
              </Button>
            </h2>

            <div className={classNames(styles.templatesTilesContainer)}>
              {groupedTemplates[c]
                .filter(
                  (t) =>
                    !displayableFeaturedTemplates.some((f) => f.id === t.id),
                )
                .sort(
                  (a, b) =>
                    b.templateGallery.precedence - a.templateGallery.precedence,
                )
                .slice(0, DISPLAY_TEMPLATE_COUNT)
                .map((t) => {
                  return (
                    <TemplatesTile
                      key={t.id}
                      avatarUrl={t.templateGallery.avatarUrl}
                      avatarShape={t.templateGallery.avatarShape}
                      backgroundColor={t.prefs.backgroundColor}
                      backgroundUrl={t.prefs.backgroundImage}
                      blurb={t.templateGallery.blurb}
                      boardId={t.id}
                      byline={t.templateGallery.byline}
                      category={t.templateGallery.category}
                      idOrganization={t.idOrganization}
                      shortLink={t.shortLink}
                      title={t.name}
                      isFeatured
                      viewCount={t.templateGallery.stats.viewCount}
                      copyCount={t.templateGallery.stats.copyCount}
                    />
                  );
                })}
            </div>
          </div>
        );
      }
    })
    .filter((item) => item);

  if (!isInAppGallery) {
    categorizedTemplates.splice(
      1,
      0,
      <WhatIsTrelloBanner key="what-is-trello-banner" />,
    );
  }

  const featuredCategories = categories.filter((c) => {
    return featuredCategoryKeys.includes(c.key);
  });

  return (
    <>
      {!isInAppGallery && <TemplatesGalleryBanner />}
      <div className={styles.headerContainer}>
        <div className={styles.featuredContainer}>
          <h1 className={styles.templateGalleryHeader}>
            <FormattedMessage
              id="templates.templates.featured-categories"
              defaultMessage="Featured categories"
              description="Featured categories"
            />
          </h1>
          {isInAppGallery && <SearchWrapper templates={templates} />}
        </div>
      </div>
      <div
        className={classNames(
          styles.categoryTilesContainer,
          styles.hideScrollbars,
        )}
      >
        {featuredCategories.map((c) => (
          <TemplatesGalleryCategoryTile
            key={c.key}
            categoryKey={c.key}
            title={intl
              .formatMessage({
                id: `templates.templates.${c.key}`,
                defaultMessage: 'Category',
                description: 'A tile linking to templates for a category',
              })
              .replace(/&amp;/g, '&')}
            url={`/templates/${c.key}`}
          />
        ))}
      </div>
      {!!displayableFeaturedTemplates.length && featuredTemplates}
      {categorizedTemplates}
    </>
  );
};
